import React, { useState, useEffect, useMemo, useRef } from 'react';
import Icon from "../icons/Icon";
import { format } from 'date-fns';
import { expandableRowProps } from '../../utils/expandableRow';
import { parsePeriodToDate } from '../../utils/periodDate';
import { calculateEmployeeFinancials, buildPayComposition } from '../../utils/payrollCalculations';
import { matchesEmployeeSearch } from '../../utils/employeeSearch';

/**
 * PayrollSummary - the Summary step (last) of the payroll wizard
 * Read-only summary page displaying aggregated data from previous steps
 * Features: Sortable columns, expandable rows, search & filter
 */
const PayrollSummary = ({ payrollRun, company, onTotalsCalculated }) => {
  // Filter & Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  // Sorting state
  const [sortField, setSortField] = useState('employeeName');
  const [sortDirection, setSortDirection] = useState('asc');

  // Expansion state
  const [expandedEmployeeId, setExpandedEmployeeId] = useState(null);

  // Filtered and sorted employees
  const [filteredEmployees, setFilteredEmployees] = useState([]);

  // Ref to store callback without triggering re-renders
  const onTotalsCalculatedRef = useRef(onTotalsCalculated);
  onTotalsCalculatedRef.current = onTotalsCalculated;

  // Helper functions to get employee data (matching Steps 2-5 data structure)
  // Format: "Last Name, First Name" for consistent sorting by last name
  const getEmployeeName = (emp) => {
    const firstName = emp.personalInfo?.firstName || '';
    const lastName = emp.personalInfo?.lastName || '';
    if (lastName && firstName) {
      return `${lastName}, ${firstName}`;
    }
    return emp.personalInfo?.fullName || lastName || firstName || emp.employeeName || 'Unknown';
  };

  const getEmployeeId = (emp) => {
    return emp.employeeId || emp.employeeCode || emp.id || '';
  };

  const getEmployeeDepartment = (emp) => {
    return emp.employment?.department || emp.department || '';
  };

  // Get unique departments (using nested path)
  const departments = useMemo(() => {
    return [...new Set((payrollRun.employees || []).map(emp => getEmployeeDepartment(emp)))].filter(Boolean).sort();
  }, [payrollRun.employees]);

  // Calculate individual employee data using shared utility
  const calculateEmployeeData = (employee) => {
    const result = calculateEmployeeFinancials(employee);
    return {
      // PRD_PayrollFigureAlignment C11 — the Gross card renders these named lines
      // rather than a hand-listed set. The hand-listed set omitted REST DAY
      // entirely, so on a run with a worked rest day the card's rows came up short
      // of its own Total Gross — the same defect the customer reported one screen
      // over, with the same ₱209.10.
      composition: buildPayComposition(employee).lines,
      // Breakdown values from utility
      ...result.breakdown.earnings,
      ...result.breakdown.deductions,
      // Time deductions (late, undertime, absence)
      late: result.breakdown.timeDeductions.late,
      undertime: result.breakdown.timeDeductions.undertime,
      absence: result.breakdown.timeDeductions.absence,
      manualEarnings: result.breakdown.adjustments.manualEarnings,
      manualDeductions: result.breakdown.adjustments.manualDeductions,
      // Totals
      grossEarnings: result.grossEarnings,  // Before time deductions
      timeDeductions: result.timeDeductions, // Late + Undertime
      grossIncome: result.gross,  // After time deductions
      totalDeductions: result.totalDeductions,
      totalLoans: result.totalLoans, // PRD_LoansManagement C8c — its own figure, not in totalDeductions
      netAdjustments: result.netAdjustments,
      netPay: result.net
    };
  };

  // Filter and sort employees
  useEffect(() => {
    if (!payrollRun.employees) {
      setFilteredEmployees([]);
      return;
    }

    let filtered = [...payrollRun.employees];

    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered.filter(emp => getEmployeeDepartment(emp) === selectedDepartment);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(emp => matchesEmployeeSearch(emp, searchTerm));
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'employeeName':
          // Sort by lastName first, then firstName as secondary sort
          aValue = (a.personalInfo?.lastName || '').toLowerCase();
          bValue = (b.personalInfo?.lastName || '').toLowerCase();
          if (aValue === bValue) {
            aValue = (a.personalInfo?.firstName || '').toLowerCase();
            bValue = (b.personalInfo?.firstName || '').toLowerCase();
          }
          break;
        case 'employeeCode':
          aValue = getEmployeeId(a);
          bValue = getEmployeeId(b);
          break;
        case 'department':
          aValue = getEmployeeDepartment(a).toLowerCase();
          bValue = getEmployeeDepartment(b).toLowerCase();
          break;
        case 'grossIncome':
          aValue = calculateEmployeeData(a).grossIncome;
          bValue = calculateEmployeeData(b).grossIncome;
          break;
        case 'deductions':
          aValue = calculateEmployeeData(a).totalDeductions;
          bValue = calculateEmployeeData(b).totalDeductions;
          break;
        case 'loans':
          aValue = calculateEmployeeData(a).totalLoans;
          bValue = calculateEmployeeData(b).totalLoans;
          break;
        case 'adjustments':
          aValue = calculateEmployeeData(a).netAdjustments;
          bValue = calculateEmployeeData(b).netAdjustments;
          break;
        case 'netPay':
          aValue = calculateEmployeeData(a).netPay;
          bValue = calculateEmployeeData(b).netPay;
          break;
        default:
          aValue = getEmployeeName(a);
          bValue = getEmployeeName(b);
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });

    setFilteredEmployees(filtered);
  }, [payrollRun.employees, selectedDepartment, searchTerm, sortField, sortDirection]);

  // Calculate totals for all filtered employees (for display with search/filter)
  const totals = useMemo(() => {
    return filteredEmployees.reduce((acc, employee) => {
      const data = calculateEmployeeData(employee);
      return {
        totalGross: acc.totalGross + data.grossIncome,
        totalDeductions: acc.totalDeductions + data.totalDeductions,
        totalLoans: acc.totalLoans + data.totalLoans,
        totalAdjustments: acc.totalAdjustments + data.netAdjustments,
        totalNet: acc.totalNet + data.netPay
      };
    }, {
      totalGross: 0,
      totalDeductions: 0,
      totalLoans: 0,
      totalAdjustments: 0,
      totalNet: 0
    });
  }, [filteredEmployees]);

  // Pass totals for all selected employees to parent (for Complete Payroll modal)
  useEffect(() => {
    if (onTotalsCalculatedRef.current && payrollRun.employees?.length) {
      const allTotals = payrollRun.employees.reduce((acc, employee) => {
        const data = calculateEmployeeData(employee);
        return {
          totalEmployees: acc.totalEmployees,
          totalGross: acc.totalGross + data.grossIncome,
          totalDeductions: acc.totalDeductions + data.totalDeductions,
          totalLoans: acc.totalLoans + data.totalLoans,
          totalAdjustments: acc.totalAdjustments + data.netAdjustments,
          totalNet: acc.totalNet + data.netPay
        };
      }, {
        totalEmployees: payrollRun.employees.length,
        totalGross: 0,
        totalDeductions: 0,
        totalLoans: 0,
        totalAdjustments: 0,
        totalNet: 0
      });
      onTotalsCalculatedRef.current(allTotals);
    }
  }, [payrollRun.employees]); // Removed onTotalsCalculated from dependencies to prevent infinite loop

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sort Icon Component
  const SortIcon = ({ field }) => {
    const isActive = sortField === field;
    const color = "white";

    if (isActive && sortDirection === "asc") {
      return (
        <Icon name="arrow-up" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    if (isActive && sortDirection === "desc") {
      return (
        <Icon name="arrow-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle" }} />
      );
    }

    return (
      <Icon name="chevrons-up-down" size={12} style={{ marginLeft: "4px", verticalAlign: "middle", opacity: 0.5 }} />
    );
  };

  // Toggle row expansion
  const toggleExpansion = (employeeId) => {
    setExpandedEmployeeId(expandedEmployeeId === employeeId ? null : employeeId);
  };

  // Get employee initials for avatar (FirstName initial + LastName initial)
  const getInitials = (emp) => {
    const first = emp.personalInfo?.firstName?.charAt(0) || '';
    const last = emp.personalInfo?.lastName?.charAt(0) || '';
    return `${first}${last}`.toUpperCase() || '??';
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Empty state
  if (!payrollRun.employees || payrollRun.employees.length === 0) {
    return (
      <div className="payroll-summary">
        <div className="summary-empty-state">
          <div className="summary-empty-state__icon"><Icon name="clipboard-list" size={32} /></div>
          <h3 className="summary-empty-state__title">No Employees Selected</h3>
          <p className="summary-empty-state__message">Please go back to select employees for this payroll run.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="payroll-summary">
      {/* Header */}
      <div className="pw-header">
        <h2 className="pw-header__title">Payroll Summary</h2>
        {payrollRun.periodStart && payrollRun.periodEnd && (
          <span className="pw-header__badge">
            {format(parsePeriodToDate(payrollRun.periodStart), 'MMM d, yyyy')} – {format(parsePeriodToDate(payrollRun.periodEnd), 'MMM d, yyyy')}
          </span>
        )}
      </div>

      {/* Controls */}
      <div className="pw-toolbar">
        <div className="pw-toolbar__controls">
          <input
            type="text"
            className="pw-toolbar__search"
            placeholder="Search by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="pw-toolbar__select"
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            <option value="all">All Departments</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>
        </div>
        <span className="pw-toolbar__count">
          Showing {filteredEmployees.length} of {payrollRun.employees.length} employees
        </span>
      </div>

      {/* Table */}
      <div className="pw-table-scroll">
        <table className="pw-table pw-table--summary">
          <thead>
            <tr>
              <th className="th-employee" onClick={() => handleSort('employeeName')}>
                EMPLOYEE<SortIcon field="employeeName" />
              </th>
              <th className="th-id" onClick={() => handleSort('employeeCode')}>
                ID<SortIcon field="employeeCode" />
              </th>
              <th className="th-department" onClick={() => handleSort('department')}>
                DEPARTMENT<SortIcon field="department" />
              </th>
              <th className="th-gross" onClick={() => handleSort('grossIncome')}>
                GROSS INCOME<SortIcon field="grossIncome" />
              </th>
              <th className="th-deductions" onClick={() => handleSort('deductions')}>
                GOV'T CONTRIBUTIONS<SortIcon field="deductions" />
              </th>
              <th className="th-loans" onClick={() => handleSort('loans')}>
                LOANS<SortIcon field="loans" />
              </th>
              <th className="th-adjustments" onClick={() => handleSort('adjustments')}>
                ADJUSTMENTS<SortIcon field="adjustments" />
              </th>
              <th className="th-net" onClick={() => handleSort('netPay')}>
                NET PAY<SortIcon field="netPay" />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredEmployees.map(employee => {
              const data = calculateEmployeeData(employee);
              const empId = getEmployeeId(employee);
              const empName = getEmployeeName(employee);
              const empDept = getEmployeeDepartment(employee);
              const isExpanded = expandedEmployeeId === empId;

              return (
                <React.Fragment key={empId || employee.id}>
                  {/* Main Row */}
                  <tr
                    className={`employee-row ${isExpanded ? 'expanded' : ''}`}
                    {...expandableRowProps(isExpanded, () => toggleExpansion(empId))}
                  >
                    <td className="pw-name-cell">
                      <div className="pw-name-cell__inner">
                        <div className="pw-avatar">{getInitials(employee)}</div>
                        <span className="pw-name-cell__text">{empName}</span>
                        <span className={`pw-chevron ${isExpanded ? 'expanded' : ''}`}>&#9660;</span>
                      </div>
                    </td>
                    <td className="td-id">{empId}</td>
                    <td className="td-department">{empDept || '-'}</td>
                    <td className="td-currency">{formatCurrency(data.grossIncome)}</td>
                    <td className="td-currency">{formatCurrency(data.totalDeductions)}</td>
                    <td className="td-currency">{formatCurrency(data.totalLoans)}</td>
                    <td className="td-currency td-adjustments">
                      {data.netAdjustments !== 0 ? (
                        <span className={data.netAdjustments > 0 ? 'positive' : 'negative'}>
                          {data.netAdjustments > 0 ? '+' : ''}{formatCurrency(data.netAdjustments)}
                        </span>
                      ) : (
                        formatCurrency(0)
                      )}
                    </td>
                    <td className="td-currency td-net">{formatCurrency(data.netPay)}</td>
                  </tr>

                  {/* Expanded Row */}
                  {isExpanded && (
                    <tr className="summary-expansion-row">
                      <td colSpan="8">
                        <div className="summary-expansion-content">
                          <div className="summary-expansion-grid">
                            {/* Gross Income Breakdown */}
                            <div className="summary-breakdown-card">
                              <div className="summary-breakdown-card__header">
                                <div className="summary-breakdown-card__icon summary-breakdown-card__icon--gross"><Icon name="wallet" size={18} /></div>
                                <span className="summary-breakdown-card__title">Gross Income</span>
                              </div>
                              {/* C11 — every component of Gross, so the rows add up to
                                  the total below them. Basic always shows; the rest
                                  only when they carry money (C12 leaves the zero
                                  policy to the renderer). */}
                              {data.composition
                                .filter((line) => line.key === 'basic' || !line.isZero)
                                .map((line) => (
                                  <div className="summary-breakdown-item" key={line.key}>
                                    <span className="summary-breakdown-item__label">
                                      {line.kind === 'timeDeduction' ? `Less: ${line.label}` : line.label}
                                      {line.qty ? ` (${line.qty})` : ''}
                                    </span>
                                    <span className={`summary-breakdown-item__value${line.amount < 0 ? ' negative' : ''}`}>
                                      {formatCurrency(line.amount)}
                                    </span>
                                  </div>
                                ))}
                              <div className="summary-breakdown-item summary-breakdown-item--total">
                                <span className="summary-breakdown-item__label">Total Gross</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.grossIncome)}</span>
                              </div>
                            </div>

                            {/* Deductions Breakdown */}
                            <div className="summary-breakdown-card">
                              <div className="summary-breakdown-card__header">
                                <div className="summary-breakdown-card__icon summary-breakdown-card__icon--deductions"><Icon name="minus" size={18} /></div>
                                <span className="summary-breakdown-card__title">Government Contributions</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">SSS</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.sss)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">PhilHealth</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.philhealth)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Pag-IBIG</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.pagibig)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Tax</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.tax)}</span>
                              </div>
                              <div className="summary-breakdown-item summary-breakdown-item--total">
                                <span className="summary-breakdown-item__label">Total Contributions</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.totalDeductions)}</span>
                              </div>
                            </div>

                            {/* Adjustments Breakdown */}
                            <div className="summary-breakdown-card">
                              <div className="summary-breakdown-card__header">
                                <div className="summary-breakdown-card__icon summary-breakdown-card__icon--adjustments"><Icon name="plus-minus" size={18} /></div>
                                <span className="summary-breakdown-card__title">Adjustments</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Manual Earnings</span>
                                <span className="summary-breakdown-item__value positive">+{formatCurrency(data.manualEarnings)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Manual Deductions</span>
                                <span className="summary-breakdown-item__value negative">-{formatCurrency(data.manualDeductions)}</span>
                              </div>
                              <div className="summary-breakdown-item summary-breakdown-item--total">
                                <span className="summary-breakdown-item__label">Net Adjustment</span>
                                <span className={`summary-breakdown-item__value ${data.netAdjustments >= 0 ? 'positive' : 'negative'}`}>
                                  {data.netAdjustments >= 0 ? '+' : ''}{formatCurrency(data.netAdjustments)}
                                </span>
                              </div>
                            </div>

                            {/* Net Pay Calculation */}
                            <div className="summary-breakdown-card summary-breakdown-card--net">
                              <div className="summary-breakdown-card__header">
                                <div className="summary-breakdown-card__icon summary-breakdown-card__icon--net"><Icon name="equals" size={18} /></div>
                                <span className="summary-breakdown-card__title">Net Pay Calculation</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Gross Income</span>
                                <span className="summary-breakdown-item__value">{formatCurrency(data.grossIncome)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Less: Gov't Contributions</span>
                                <span className="summary-breakdown-item__value negative">-{formatCurrency(data.totalDeductions)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Less: Loans</span>
                                <span className="summary-breakdown-item__value negative">-{formatCurrency(data.totalLoans)}</span>
                              </div>
                              <div className="summary-breakdown-item">
                                <span className="summary-breakdown-item__label">Plus/Less: Adjustments</span>
                                <span className={`summary-breakdown-item__value ${data.netAdjustments >= 0 ? 'positive' : 'negative'}`}>
                                  {data.netAdjustments >= 0 ? '+' : ''}{formatCurrency(data.netAdjustments)}
                                </span>
                              </div>
                              <div className="summary-breakdown-item summary-breakdown-item--total">
                                <span className="summary-breakdown-item__label">NET PAY</span>
                                <span className="summary-breakdown-item__value summary-net-pay-value">{formatCurrency(data.netPay)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredEmployees.length === 0 && (
          <div className="summary-no-results">
            <p>No employees found matching your criteria</p>
          </div>
        )}
        </div>

        {/* Footer */}
        <div className="pw-footer">
          <div className="pw-footer__stats">
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Employees</span>
              <span className="pw-footer__stat-value">{filteredEmployees.length}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Gross</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.totalGross)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Government Contributions</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.totalDeductions)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Loans</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.totalLoans)}</span>
            </div>
            <div className="pw-footer__stat">
              <span className="pw-footer__stat-label">Total Adjustments</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.totalAdjustments)}</span>
            </div>
            <div className="pw-footer__stat pw-footer__stat--total">
              <span className="pw-footer__stat-label">Total Net Pay</span>
              <span className="pw-footer__stat-value">{formatCurrency(totals.totalNet)}</span>
            </div>
          </div>
          <span className="pw-footer__hint">Click on any row to expand</span>
        </div>
    </div>
  );
};

export default PayrollSummary;
